/**
 * 触发日志抽屉（docs/schedule-v3.md §5.4）。
 *
 * 点「任务管理 → 定时列表 → 查看日志」打开本抽屉。内容来自
 * ``GET /api/v1/schedules/{id}/logs``，列：触发时间 / 结果（success/failed Tag）/
 * Job ID（点击跳 Job 详情）/ 耗时 / 错误信息（错误类型 Tag + error_msg 折叠展开）+
 * 触发当时的 payload 快照。
 *
 * 视觉与 ``SceneCommentDrawer`` 风格保持一致（refined editorial）。
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Drawer,
  Table,
  Tag,
  Space,
  Tooltip,
  Empty,
  Spin,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import {
  listScheduleLogs,
  type ScheduleLogItem,
  type ScheduleErrorType,
} from '@apis/simulation';

/** 错误类型 → 中文展示 + 颜色（失败原因分类，6 档）。 */
const ERROR_TYPE_META: Record<ScheduleErrorType, { label: string; color: string }> = {
  branch_not_found: { label: '分支不存在', color: 'orange' },
  repo_access:     { label: '仓库无权限', color: 'volcano' },
  compile_failed:  { label: '编译失败',   color: 'magenta' },
  dispatch_failed: { label: '调度失败',   color: 'red' },
  timeout:          { label: '超时',       color: 'gold' },
  unknown:          { label: '未知错误',   color: 'default' },
};

/** 失败原因在 Tag 上展示，error_msg 完整原文过长时折叠到 Tooltip。 */
function ErrorCell({ record }: { record: ScheduleLogItem }) {
  if (record.trigger_status === 'success') {
    return <span style={{ color: '#c4c6cc' }}>-</span>;
  }
  const typeKey = (record.error_type || 'unknown') as ScheduleErrorType;
  const meta = ERROR_TYPE_META[typeKey] ?? ERROR_TYPE_META.unknown;
  const errMsg = (record.error_msg || '').trim();
  if (!errMsg) {
    return <Tag color={meta.color}>{meta.label}</Tag>;
  }
  // error_msg 截断 500 字（后端已截），Tooltip 完整展示
  return (
    <Tooltip title={errMsg} placement="topLeft">
      <Space size={4} wrap>
        <Tag color={meta.color}>{meta.label}</Tag>
        <span style={{
          color: '#5e6ad2',
          fontSize: 12,
          maxWidth: 320,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          verticalAlign: 'middle',
        }}>
          {errMsg}
        </span>
      </Space>
    </Tooltip>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 排程 ID。 */
  scheduleId: number | null;
  /** 排程名（用于抽屉标题）。 */
  scheduleName?: string | null;
}

const PAGE_SIZE = 20;

export default function ScheduleLogDrawer({ open, onClose, scheduleId, scheduleName }: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ScheduleLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async (id: number, current: number) => {
    setLoading(true);
    try {
      const res = await listScheduleLogs(id, { page: current, pageSize: PAGE_SIZE });
      setData(res.items);
      setTotal(res.total);
      setPage(res.page);
    } catch (err) {
      console.error('加载触发日志失败:', err);
      message.error('加载触发日志失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || scheduleId == null) return;
    setData([]);
    setTotal(0);
    setPage(1);
    load(scheduleId, 1);
  }, [open, scheduleId, load]);

  const columns: ColumnsType<ScheduleLogItem> = [
    {
      title: '触发时间',
      dataIndex: 'fired_at',
      key: 'fired_at',
      width: 170,
      render: (text: string) => {
        if (!text) return '-';
        try {
          return new Date(text).toLocaleString('zh-CN');
        } catch {
          return text;
        }
      },
    },
    {
      title: '结果',
      dataIndex: 'trigger_status',
      key: 'trigger_status',
      width: 90,
      align: 'center',
      render: (status: string) => {
        if (status === 'success') {
          return <Tag color="green">成功</Tag>;
        }
        return <Tag color="red">失败</Tag>;
      },
    },
    {
      title: 'Job ID',
      dataIndex: 'job_id',
      key: 'job_id',
      width: 110,
      align: 'center',
      render: (jobId: number | null) => {
        if (jobId == null) return <span style={{ color: '#c4c6cc' }}>-</span>;
        return (
          <Link
            to={`/job-tasks/${jobId}`}
            style={{ color: '#5e6ad2', cursor: 'pointer' }}
          >
            {jobId}
          </Link>
        );
      },
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 90,
      align: 'center',
      render: (ms: number | null) => {
        if (ms == null) return <span style={{ color: '#c4c6cc' }}>-</span>;
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
      },
    },
    {
      title: '错误信息',
      key: 'error',
      render: (_: unknown, record: ScheduleLogItem) => <ErrorCell record={record} />,
    },
  ];

  return (
    <Drawer
      title={`触发日志${scheduleName ? ` - ${scheduleName}` : ''}`}
      open={open}
      onClose={onClose}
      width={900}
      destroyOnHidden
    >
      <Spin spinning={loading}>
        {data.length === 0 && !loading ? (
          <Empty description="暂无触发记录" style={{ marginTop: 60 }} />
        ) : (
          <Table
            rowKey="id"
            dataSource={data}
            columns={columns}
            size="small"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (t) => `总共 ${t} 条`,
              onChange: (p) => {
                if (scheduleId != null) load(scheduleId, p);
              },
            }}
          />
        )}
      </Spin>
    </Drawer>
  );
}
